import React from 'react';
import { Table, Space, Flex, Form, Modal, Input, InputNumber, Select, Tag, Button, message, Alert } from 'antd';
import dayjs from 'dayjs';
import usePagination from '../../../components/pagination';

const typeMap = {
    1: '个人',
    2: '公司',
    3: '个体户'
}
const statusMap = {
    0: '注册中',
    1: '待审核',
    2: '生效',
    3: '禁用'
}

export default () => {
    const [dataSource, setDataSource] = React.useState([]);
    const [status, setStatus] = React.useState('');
    const [keyword, setKeyword] = React.useState('');
    const [adjusting, setAdjusting] = React.useState(null);
    const [feeForm] = Form.useForm();
    const [goodsModal, setGoodsModal] = React.useState(null);
    const [goodsData, setGoodsData] = React.useState([]);
    const [goodsLoading, setGoodsLoading] = React.useState(false);
    const [goodsTotal, setGoodsTotal] = React.useState(0);
    const [goodsKeyword, setGoodsKeyword] = React.useState('');
    const [goodsPage, setGoodsPage] = React.useState(1);
    const [goodsPageSize, setGoodsPageSize] = React.useState(10);
    const { pagination, current, pageSize, setTotal } = usePagination({
        onChange: (page, pageSize) => {
            getList(page, pageSize).then((res) => {
                setDataSource(res.rows);
                setTotal(res.count);
            });
        },
    });

    const getList = async (page = 1, pageSize = 10) => {
        const params = new URLSearchParams({ page, pageSize });
        if (status !== '') params.append('status', status);
        if (keyword) params.append('keyword', keyword);
        const response = await fetch(`/api/sys/account/list?${params.toString()}`);
        const result = await response.json();
        if (result && result.success) {
            return result.data;
        }
        return { rows: [], count: 0 };
    }

    React.useEffect(() => {
        getList().then((res) => {
            setDataSource(res.rows);
            setTotal(res.count);
        });
    }, [])

    const refreshList = () => {
        getList(current, pageSize).then((res) => {
            setDataSource(res.rows);
            setTotal(res.count);
        });
    }

    const handleSearch = () => {
        getList(1, pageSize).then((res) => {
            setDataSource(res.rows);
            setTotal(res.count);
        });
    }

    // 有效年费：自定义优先，否则版本默认
    const effectiveYearFee = (r) => {
        if (r.yearFee !== null && r.yearFee !== undefined) return Number(r.yearFee);
        return r.version?.price;
    }
    // 有效费率(小数)：自定义优先，否则版本默认
    const effectiveRate = (r) => {
        if (r.withdrawRate !== null && r.withdrawRate !== undefined) return r.withdrawRate;
        return (r.version?.activeFee ?? r.version?.fee);
    }

    const openAdjust = (r) => {
        setAdjusting(r);
        feeForm.setFieldsValue({
            yearFee: (r.yearFee !== null && r.yearFee !== undefined) ? Number(r.yearFee) : undefined,
            withdrawRate: (r.withdrawRate !== null && r.withdrawRate !== undefined) ? Number(r.withdrawRate) * 100 : undefined,
        });
    }

    const submitAdjust = async () => {
        const values = await feeForm.validateFields();
        const payload = { accId: adjusting.accId };

        payload.yearFee = (values.yearFee === undefined || values.yearFee === null) ? null : Number(values.yearFee);
        payload.withdrawRate = (values.withdrawRate === undefined || values.withdrawRate === null) ? null : Number(values.withdrawRate) / 100;

        const response = await fetch('/api/sys/account/fee', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        const result = await response.json();

        if (result && result.success) {
            message.success('调整成功');
            setAdjusting(null);
            refreshList();
        } else {
            message.error(result.data || '调整失败');
        }
    }

    const loadGoods = async (accId, page = 1, pageSize = 10, kw) => {
        setGoodsLoading(true);
        try {
            const params = new URLSearchParams({ accId, page, pageSize });
            const search = kw !== undefined ? kw : goodsKeyword;
            if (search) params.append('keyword', search);
            const response = await fetch(`/api/sys/account/goods?${params.toString()}`);
            const result = await response.json();
            if (result && result.success) {
                setGoodsData(result.data.rows);
                setGoodsTotal(result.data.count);
            }
        } finally {
            setGoodsLoading(false);
        }
    }

    const openGoods = (r) => {
        setGoodsModal({ accId: r.accId, title: r.title });
        setGoodsData([]);
        setGoodsTotal(0);
        setGoodsKeyword('');
        setGoodsPage(1);
        loadGoods(r.accId, 1, goodsPageSize, '');
    }

    const columns = [
        {
            title: '账户ID',
            dataIndex: 'accId',
            width: 130,
        },
        {
            title: '租户名称',
            dataIndex: 'title',
            render: (text, r) => {
                return <div>
                    <div>{text}</div>
                    {r.companyName && <div style={{ fontSize: 12, color: '#999' }}>{r.companyName}</div>}
                    {r.realname && <div style={{ fontSize: 12, color: '#999' }}>{r.realname}</div>}
                </div>
            }
        },
        {
            title: '电话',
            dataIndex: 'phoneNo',
        },
        {
            title: '版本',
            dataIndex: ['version', 'title'],
            render: (text) => text || '-',
        },
        {
            title: '状态',
            dataIndex: 'status',
            render: (text) => {
                return <span style={{ color: +text === 1 ? 'red' : '' }}>{statusMap[+text]}</span>
            }
        },
        {
            title: '注册日期',
            dataIndex: 'createTime',
            render: (text) => dayjs(text).format('YYYY-MM-DD'),
        },
        {
            title: '年费(元/年)',
            dataIndex: 'yearFee',
            render: (text, r) => {
                const custom = r.yearFee !== null && r.yearFee !== undefined;
                const v = effectiveYearFee(r);
                return <Space>
                    {v === undefined || v === null ? '-' : v}
                    {custom && <Tag color='orange'>自定义</Tag>}
                </Space>
            }
        },
        {
            title: '费率',
            dataIndex: 'withdrawRate',
            render: (text, r) => {
                const custom = r.withdrawRate !== null && r.withdrawRate !== undefined;
                const v = effectiveRate(r);
                return <Space>
                    {v === undefined || v === null ? '-' : `${(Number(v) * 100)}%`}
                    {custom && <Tag color='orange'>自定义</Tag>}
                </Space>
            }
        },
        {
            title: '近1年销售额(元)',
            dataIndex: 'salesAmount',
            render: (text) => {
                const v = Number(text) || 0;
                return (v / 100).toFixed(2);
            }
        },
        {
            title: '商品数量',
            dataIndex: 'goodsCount',
            render: (text) => Number(text) || 0,
        },
        {
            title: '操作',
            dataIndex: 'status',
            fixed: 'right',
            render: (text, r) => {
                return <Space>
                    <a onClick={() => openGoods(r)}>查看商品</a>
                    <a onClick={() => openAdjust(r)}>年费/费率调整</a>
                </Space>
            }
        },
    ]

    const goodsTypeMap = { NORMAL: '服务', GOODS: '实物', TIMES_CARD: '次卡' };
    const goodsStatusMap = { 0: '草稿', 1: '生效中', 2: '下架' };
    const goodsColumns = [
        { title: '商品ID', dataIndex: 'goodsId', width: 120 },
        { title: '商品名称', dataIndex: 'title' },
        { title: '类型', dataIndex: 'type', render: (t) => goodsTypeMap[t] || t },
        { title: '状态', dataIndex: 'status', render: (t) => goodsStatusMap[+t] || t },
        { title: '价格(元)', dataIndex: 'price', render: (t) => (t === null || t === undefined) ? '-' : (Number(t) / 100).toFixed(2) },
        { title: '库存', dataIndex: 'inventory', render: (t) => (t === null || t === undefined) ? '-' : t },
        { title: '销量', dataIndex: 'realSaleNum', render: (t) => (t === null || t === undefined) ? '-' : t },
        { title: '店铺', dataIndex: 'shopNames', render: (t) => (t && t.length) ? t.join('、') : '-' },
        { title: '创建时间', dataIndex: 'createTime', render: (t) => t ? dayjs(t).format('YYYY-MM-DD HH:mm') : '-' },
    ];

    return (
        <div>
            <h1>租户管理</h1>
            <Flex gap={10} style={{ marginBottom: 12 }} align='center'>
                <Select
                    style={{ width: 140 }}
                    placeholder='全部状态'
                    value={status || undefined}
                    allowClear
                    options={[
                        { value: '0', label: '注册中' },
                        { value: '1', label: '待审核' },
                        { value: '2', label: '生效' },
                        { value: '3', label: '禁用' },
                    ]}
                    onChange={(v) => setStatus(v || '')}
                />
                <Input
                    style={{ width: 200 }}
                    placeholder='搜索租户名/电话/公司'
                    allowClear
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onPressEnter={handleSearch}
                />
                <Button type='primary' onClick={handleSearch}>查询</Button>
            </Flex>
            <Table rowKey={'accId'} columns={columns} dataSource={dataSource} scroll={{ x: 'max-content' }} pagination={pagination} />

            <Modal
                title={`年费/费率调整 - ${adjusting?.title || ''}`}
                open={!!adjusting}
                onOk={submitAdjust}
                onCancel={() => setAdjusting(null)}
                destroyOnClose
            >
                <Alert message='留空表示恢复版本默认；费率单位是百分比(如 5 表示 5%)' type='info' showIcon style={{ marginBottom: 16 }} />
                <Form form={feeForm} labelCol={{ span: 6 }}>
                    <Form.Item label='年费' name='yearFee'>
                        <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder='跟随版本' addonAfter='元/年' />
                    </Form.Item>
                    <Form.Item label='费率' name='withdrawRate'>
                        <InputNumber style={{ width: '100%' }} min={0} max={100} precision={2} step={0.1} placeholder='跟随版本' addonAfter='%' />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`商品列表 - ${goodsModal?.title || ''}`}
                open={!!goodsModal}
                width={1000}
                footer={null}
                onCancel={() => setGoodsModal(null)}
                destroyOnClose
            >
                <Flex gap={10} style={{ marginBottom: 12 }} align='center'>
                    <Input
                        style={{ width: 220 }}
                        placeholder='搜索商品名'
                        allowClear
                        value={goodsKeyword}
                        onChange={(e) => setGoodsKeyword(e.target.value)}
                        onPressEnter={() => loadGoods(goodsModal.accId, 1, goodsPageSize)}
                    />
                    <Button type='primary' onClick={() => loadGoods(goodsModal.accId, 1, goodsPageSize)}>查询</Button>
                </Flex>
                <Table
                    rowKey='goodsId'
                    columns={goodsColumns}
                    dataSource={goodsData}
                    loading={goodsLoading}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        current: goodsPage,
                        pageSize: goodsPageSize,
                        total: goodsTotal,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 个商品`,
                        onChange: (page, size) => {
                            setGoodsPage(page);
                            setGoodsPageSize(size);
                            loadGoods(goodsModal.accId, page, size);
                        },
                    }}
                />
            </Modal>
        </div>
    )
}
